import React, { Component } from 'react';
import lodash from 'lodash';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter, Link } from 'react-router-dom';
import { Table, Card, Button } from 'antd';
import { injectIntl } from 'react-intl';
import RestManager from '@util/RestManager';
import { BASE_URL, ENDPOINTS } from '@constants/UHEEndpoints';
import UheHelper from 'util/UheHelper';

import IntlMessages from 'util/IntlMessages';
import ListingsTopFilter from '@filters/ListingsTopFilter';
import ListingsTableInputFilter from '@filters/ListingsTableInputFilter';
import OrganizationCell from '@components/tables/cells/OrganizationCell';
import CustomerCell from '@components/tables/cells/CustomerCell';
import FacilityCell from '@components/tables/cells/FacilityCell';
import UnitCell from '@components/tables/cells/UnitCell';
import AddressCell from '@components/tables/cells/AddressCell';
import ActionsCell from '@components/tables/cells/ActionsCell';
import NonUheUnitCell from '@components/tables/cells/NonUheUnitCell';
import ExportCsv from '@components/ExportCsv/ExportCsv';
import { setSubtitle } from '@uhe_actions/SubtitleActions';

import { deleteNonUheBedCart } from '@uhe_actions/configuration/bedsCarts/bedsCartsActions';
import { onFetchData } from '@uhe_actions/monitoring/UHEActions';
import {
  LISTING_TABLES_PAGE_SIZE, TABLE_FILTER_PREFIX, TOP_FILTER_PREFIX, APP_PAGES_CONTEXT, IS_EDIT_PAGE_REGEX,
} from '@constants/UHESettings';
import { withLastLocation } from 'react-router-last-location';
import {
  getColumnSorting, setTableData, getCurrentSort, saveDefaultSortOnClick,
} from '@util/UheHelper';
import { shouldResetPaginationSuccess } from '@actions';
import { DownOutlined } from '@ant-design/icons';
import { shouldBeAbleToImport, shouldBeAbleToExport, shouldBeAbleToAdd } from '@util/UheRoleChecker';
import { ImportButton, ExportButton } from '@components/uhe/listings/Buttons';

/**
 * Renders non Uhe table
 */
class NonUheTable extends Component {
  static pageContext = APP_PAGES_CONTEXT.nonUheUnits;

  /**
   * Adapt data returned from the server
   * @param  {Array<Object>} data data
   * @return {Array<Object>} data
   */
  static dataAdapter(data = []) {
    const adaptedData = [];
    data.forEach((value, index) => {
      adaptedData.push({
        key: index,
        organization: {
          id: value.organization_id,
          name: value.organization_name,
        },
        customer: {
          id: value.customer_id,
          organizationId: value.organization_id,
          name: value.customer_name,
        },
        facility: {
          id: value.facility_id,
          organizationId: value.organization_id,
          customerId: value.customer_id,
          name: value.facility_name,
        },
        unit: {
          id: value.unit_id,
          organizationId: value.organization_id,
          customerId: value.customer_id,
          facilityId: value.facility_id,
          name: value.unit_name,
        },
        bedCart: {
          id: value.device_id,
          name: value.cart_name,
          customerId: value.customer_id,
        },
        protocol: value.dial_out_type,
        address: {
          id: value.device_id,
          name: value.alias,
        },
        actions: {
          device_id: value.device_id,
        },
      });
    });

    return adaptedData;
  }

  /**
  * HomePage Constructor
  * @param {object} props HomePage Props
  */
  constructor(props) {
    super(props);
    this.state = {
      tableData: {
        organization: {
          currSort: 'asc',
          isClicked: false,
        },
        customer: {
          currSort: 'asc',
          isClicked: false,
        },
        facility: {
          currSort: 'asc',
          isClicked: false,
        },
        unit: {
          currSort: 'asc',
          isClicked: false,
        },
        bedCart: {
          currSort: 'asc',
          isClicked: false,
        },
        protocol: {
          currSort: 'asc',
          isClicked: false,
        },
        address: {
          currSort: 'asc',
          isClicked: false,
        },
      },
      showMore: 2,
    };
    this.filterTypes = {};
    this.data = [];
    this.columns = [];

    const { history } = this.props;

    this.topFilterMap = {
      [`${TOP_FILTER_PREFIX}organization`]: 'organization_id',
      [`${TOP_FILTER_PREFIX}customer`]: 'customer_id',
      [`${TOP_FILTER_PREFIX}facility`]: 'facility_id',
      [`${TOP_FILTER_PREFIX}unit`]: 'unit_id',
    };

    this.tableFilterMap = {
      organization: 'organization_name',
      customer: 'customer_name',
      facility: 'facility_name',
      unit: 'unit_name',
      bedCart: 'cart_name',
      protocol: 'dial_out_type',
      address: 'alias',
    };

    this.tableKeys = [
      'organization',
      'customer',
      'facility',
      'unit',
      'bedCart',
      'protocol',
      'address',
    ];

    this.topFilters = [
      {
        placeholder: 'uhe.listingsTopFilter.inputLabels.byOrganization',
        fieldNames: { label: 'name', value: 'id' },
        showSearch: true,
        key: 'organization',
        shouldSort: true,
      },
      {
        placeholder: 'uhe.listingsTopFilter.inputLabels.byCustomer',
        fieldNames: { label: 'name', value: 'id' },
        showSearch: true,
        key: 'customer',
        shouldSort: true,
      },
      {
        placeholder: 'uhe.listingsTopFilter.inputLabels.byFacility',
        fieldNames: { label: 'name', value: 'id' },
        showSearch: true,
        key: 'facility',
        shouldSort: true,
      },
      {
        placeholder: 'uhe.listingsTopFilter.inputLabels.byUnit',
        fieldNames: { label: 'name', value: 'id' },
        showSearch: true,
        key: 'unit',
        shouldSort: true,
      },
    ];

    this.onPageChange = this.onPageChange.bind(this);
    this.setTableData = setTableData.bind(this);
    this.history = history;
    this.qParams = new URLSearchParams(this.history.location.search);
    this.defaultSorted = true;
    const { location, subtitle, setSubtitle } = props;
    const { tableData } = this.state;

    this.tableKeys.forEach((value, index) => {
      this.columns.push({
        title: (cellData) => (
          <ListingsTableInputFilter
            cellData={cellData}
            tableData={tableData}
            setTableData={this.setTableData}
            title={`uhe.table.${value}`}
            dataKey={value}
            showFilter={!(value === 'actions')}
          />
        ),
        sorter: (value === 'actions') ? false : { multiple: index },
        defaultSortOrder: value ? getColumnSorting(value, location) : false,
        align: index > 3 ? 'center' : 'left',
        minWidth: 200,
        dataIndex: value,
        render: (content) => this.cellRenderer(content, value),
      });
    });

    if (subtitle && subtitle.langId !== 'sidebar.SBAPS') {
      setSubtitle('sidebar.SBAPS');
    }
    this.tableFooterRef = React.createRef();
  }

  /**
   * componentDidMount() is invoked immediately after
   * a component is mounted (inserted into the tree)
   * @returns {void} void
   */
  componentDidMount() {
    this.tableFooterRef.current.parentNode.parentNode.addEventListener('scroll', this.handleScroll, true);
    const {
      lastLocation,
      pagination,
      shouldResetPaginationFlag,
      resetPaginationSuccess,
    } = this.props;

    if (!IS_EDIT_PAGE_REGEX.test(lastLocation?.pathname) || shouldResetPaginationFlag) {
      this.onPageChange(1);
      resetPaginationSuccess();
      return;
    }

    this.onPageChange(pagination?.current || 1);
  }

  /**
   * Updates table on location change
   * @param {Object} prevProps previous props
   * @returns {void} void
   */
  componentDidUpdate(prevProps) {
    const { location } = this.props;
    if (location.search !== prevProps.location.search) {
      this.qParams = new URLSearchParams(location.search);
      this.onPageChange(1);
    }
  }

  /**
   * Remove Event Listener
   * @returns {void}
   */
  componentWillUnmount() {
    this.tableFooterRef.current.parentNode.parentNode.removeEventListener('scroll', this.handleScroll);
  }

  /**
   * onPageChange
   * @param  {number} page current page
   * @return {void}
   */
  onPageChange(page) {
    this.currentPage = page - 1;
    const currSort = this.qParams.getAll('sort') || [];
    const filter = [];
    const defaultSort = ['organization_name,asc', 'customer_name,asc', 'facility_name,asc', 'unit_name,asc', 'cart_name,asc', 'dial_out_type,asc', 'alias,asc'];

    lodash.forOwn(this.topFilterMap, (value, key) => {
      const filterParam = this.qParams.get(key);
      if (filterParam) {
        filter.push(`${value}=${filterParam}`);
      }
    });

    lodash.forOwn(this.tableFilterMap, (value, key) => {
      const filterParam = this.qParams.get(`${TABLE_FILTER_PREFIX}${key}`);
      if (filterParam) {
        if (this.filterTypes[key]
          && this.filterTypes[key].type === 'dropdown'
        ) {
          filter.push(`${value}=${encodeURIComponent(filterParam)}`);
        } else {
          filter.push(`${value}~=${encodeURIComponent(`%${filterParam}%`)}`);
        }
      }
    });

    let sort = getCurrentSort(currSort, this.tableFilterMap);

    if (this.defaultSorted && currSort.length === 0) {
      sort = defaultSort;
    } else if (currSort.length) {
      const customSort = getCurrentSort(currSort, this.tableFilterMap);
      const nonCustomFilteredColumns = saveDefaultSortOnClick(defaultSort, customSort);
      sort = [...customSort, ...nonCustomFilteredColumns];
      this.defaultSorted = false;
    }

    this.filter = filter;
    this.sort = sort;

    this.setState({ showMore: page + 1 });

    this.props.onFetchData(page - 1, sort, filter);
  }

  /**
   * Get endpoint url for data export
   * @returns {string} csv url
   */
  getCsvUrl = () => {
    const token = RestManager.getToken();

    const filterQueryString = this.filter && this.filter.length ? `&${this.filter.join('&')}` : '';
    const sortingQueryString = this.sorting && this.sorting.length
      ? `&sort=${this.sorting.join('&sort=')}`
      : '';

    const tokenParam = `&token=${token}`;
    return `${BASE_URL}${ENDPOINTS.bedCart.nonUheCsv}${filterQueryString}${sortingQueryString}${tokenParam}`;
  }

  /**
   * Check the default sort order for table
   * @returns {string} checkOrder
   */
  checkSortTableOrder() {
    const { location } = this.props;
    let checkOrder = location.search.split('%2C')[1];

    if (checkOrder === 'asc') {
      checkOrder = 'ascend';
    }
    if (checkOrder === 'desc') {
      checkOrder = 'descend';
    }
    if (!checkOrder) {
      checkOrder = false;
    }

    return checkOrder;
  }

  /**
   * Renders different cell type depending on the column
   * @param  {Object} content Content
   * @param  {string} key Key
   * @return {ReactElement|Object} Return cell content
   */
  cellRenderer(content, key) {
    let cell;
    const {
      intl, history, loggedUser, location, onDeleteNonUheBedCart,
    } = this.props;

    switch (key) {
      case 'organization':
        cell = <OrganizationCell content={content} />;
        break;
      case 'customer':
        cell = <CustomerCell content={content} />;
        break;
      case 'facility':
        cell = <FacilityCell content={content} />;
        break;
      case 'unit':
        cell = <UnitCell content={content} location={location} />;
        break;
      case 'bedCart':
        cell = <NonUheUnitCell content={content} />;
        break;
      case 'address':
        cell = <AddressCell editLink={`/configuration/beds-carts/non-uhe-units/edit/${content.id}`} content={content} />;
        break;
      case 'actions':
        cell = (
          <ActionsCell
            page={APP_PAGES_CONTEXT.nonUheUnits}
            loggedUser={loggedUser}
            intl={intl}
            content={content}
            editLink={`/configuration/beds-carts/non-uhe-units/edit/${content.device_id}`}
            deleteAction={() => {
              onDeleteNonUheBedCart(
                content.device_id,
                this.currentPage,
                this.sort,
                this.filter,
                history,
              );
            }}
          />
        );
        break;

      default:
        cell = content;
    }

    return cell;
  }

  /**
  * Render Add Button
  * @returns {JSX} render add button
  */
  renderAddButton = () => {
    const { loggedUser } = this.props;
    const {
      isCaregilitySystemAdmin, isOrganizationAdmin, isCustomerAdmin, isFacilityAdmin,
      isTechnicianAdmin,
    } = loggedUser;

    if (isCaregilitySystemAdmin
      || isOrganizationAdmin
      || isCustomerAdmin
      || isFacilityAdmin
      || isTechnicianAdmin) {
      return (
        <Link
          to="/configuration/beds-carts/non-uhe-units/new"
          shape="circle"
          className="page-icons page-icon-plus"
        >
          <i className="icon icon-add" />
          <span>
            <IntlMessages id="common.addText" />
          </span>
        </Link>
      );
    }

    return null;
  }

  /**
  * Render Export Csv Button
  * @returns {JSX} render export button
  */
  renderExportCsvButton = () => {
    const { loggedUser } = this.props;
    const {
      isCaregilitySystemAdmin, isCaregilityAdmin, isOrganizationAdmin,
      isCustomerAdmin, isFacilityAdmin,
    } = loggedUser;

    if (isCaregilitySystemAdmin
      || isCaregilityAdmin
      || isOrganizationAdmin
      || isCustomerAdmin
      || isFacilityAdmin) {
      return (
        <ExportCsv
          getCsvUrl={UheHelper.getCsvUrl(
            this.filter,
            this.sorting,
            ENDPOINTS.ExportCsv.nonUhe,
          )}
        />
      );
    }
  }

  /**
   * Render Add Button
  * @returns {JSX} component
  */
  renderImportButton = () => {
    const { loggedUser } = this.props;
    return (
      <ImportButton
        visible={shouldBeAbleToImport(loggedUser, NonUheTable.pageContext)}
        url="/configuration/beds-carts/non-uhe-units/bulk/upload"
      />
    );
  }

  /**
  * Handles horizontal scroll in table
  * @param {event} event table
  * @returns {void}
  */
  handleScroll = (event) => {
    const currentScroll = event.target.scrollLeft;
    if (this.tableFooterRef.current) {
      this.tableFooterRef.current.parentNode.style.transform = `translateX(${currentScroll}px)`;
    }
  };

  /**
  * Render table
  * @returns {JSX} render table
  */
  renderTable = () => {
    const {
      pagination, data, loading, loggedUser,
    } = this.props;
    pagination.onChange = this.onPageChange;
    const { location } = this.props;
    const { tableData, showMore } = this.state;
    if (loggedUser.roles) {
      const {
        isCaregilitySystemAdmin, isTechnicianAdmin, isOrganizationAdmin, isCustomerAdmin,
        isFacilityAdmin,
      } = loggedUser;

      if (isCaregilitySystemAdmin
        || isOrganizationAdmin
        || isCustomerAdmin
        || isFacilityAdmin
        || isTechnicianAdmin) {
        this.tableFilterMap.actions = 'actions';
        this.tableKeys = Object.keys(this.tableFilterMap);
        if (this.columns.length <= 7) {
          this.tableKeys.filter((key) => key === 'actions').forEach((value, index) => {
            const filter = this.filterTypes[value] || {};
            this.columns.push({
              title: (cellData) => (
                <ListingsTableInputFilter
                  cellData={cellData}
                  tableData={tableData}
                  setTableData={this.setTableData}
                  title={`uhe.table.${value}`}
                  dataKey={value}
                  showFilter={!(value === 'actions')}
                />
              ),
              sorter: (value === 'actions') ? false : { multiple: index },
              defaultSortOrder: value ? getColumnSorting(value, location) : false,
              align: index > 3 ? 'center' : 'left',
              minWidth: 200,
              dataIndex: value,
              render: (content) => this.cellRenderer(content, value),
            });
          });
        }
      }

      return (
        <Table
          bordered
          className="gx-table-responsive custom-scrollbar"
          columns={this.columns}
          dataSource={NonUheTable.dataAdapter(data)}
          pagination={false}
          loading={loading}
          footer={() => (
            <div className="table-footer" ref={this.tableFooterRef}>
              <Button type="text" onClick={() => this.onPageChange(showMore)}>
                <IntlMessages id="common.showMore" />
                <DownOutlined />
              </Button>
            </div>
          )}
        />
      );
    }
  }

  /**
   * Render NonUheUnits Component
   * @returns {JSX} NonUheUnits Component
   */
  render() {
    const { pagination } = this.props;
    pagination.onChange = this.onPageChange;

    return (
      <div className="dashboard configuration">
        <div className="customers-filter-box">
          <div className="beds-carts">
            <Card className="filter-boxes gx-card" title={<IntlMessages id="uhe.title.filters" />}>
              <ListingsTopFilter filters={this.topFilters} />
            </Card>
            <div className="uhe-table-header">
              <div className="buttons-container">
                {this.renderAddButton()}
                {this.renderImportButton()}
                {this.renderExportCsvButton()}
              </div>
              <p>
                <IntlMessages id="uhe.table.matchingResults" />
                <span>{pagination.total}</span>
              </p>
            </div>
            <div className="uhe-table">
              {this.renderTable()}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

NonUheTable.defaultProps = {
  lastLocation: {
    pathname: '/',
    search: '',
    hash: '',
    state: undefined,
    key: '',
  },
  shouldResetPaginationFlag: false,
};

NonUheTable.propTypes = {
  data: PropTypes.array,
  pagination: PropTypes.object,
  intl: PropTypes.object,
  history: PropTypes.object,
  location: PropTypes.object,
  loading: PropTypes.bool,
  loggedUser: PropTypes.shape().isRequired,
  lastLocation: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
    hash: PropTypes.string,
    state: undefined,
    key: PropTypes.string,
  }),
  shouldResetPaginationFlag: PropTypes.bool,
  resetPaginationSuccess: PropTypes.func.isRequired,
  onDeleteNonUheBedCart: PropTypes.func,
};

/**
 * mapStateToProps function
 * @param {Object} ConfigurationUnits object
 * @param {Object} ConfigurationUsers object
 * @param {Object} common object
 * @returns {Object} object
 */
const mapStateToProps = ({
  MonitoringStandards, subtitle, ConfigurationUsers, common,
}) => {
  const { list, page } = MonitoringStandards.table || {};
  const { loading } = MonitoringStandards;
  const { shouldResetPagination } = common;

  const pagination = {
    total: page.totalElements || 0,
    current: page.number + 1 || 0,
    pageSize: LISTING_TABLES_PAGE_SIZE,
    defaultCurrent: 1,
  };

  return {
    data: list,
    pagination,
    loading,
    subtitle,
    loggedUser: ConfigurationUsers.ownUser,
    shouldResetPaginationFlag: shouldResetPagination,
  };
};

/**
 * mapDispatchToProps function
 * @param {Function} dispatch dispatch
 * @returns {Object} object
 */
const mapDispatchToProps = (dispatch) => ({
  onDeleteNonUheBedCart: (device_id, page, sorting, filter, history) => dispatch(
    deleteNonUheBedCart({
      device_id, page, sorting, filter, history,
    }),
  ),
  onFetchData: (page, sort, filter) => dispatch(onFetchData(page, sort, filter, 'standards')),
  setSubtitle: (langId) => dispatch(setSubtitle(langId)),
  resetPaginationSuccess: () => dispatch(shouldResetPaginationSuccess()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(injectIntl(withLastLocation(withRouter(NonUheTable))));
